// Fetch wrapper for the Helm web console — Nexus gateway-native auth, same shape as StoreFront's.
//
//  - POST /auth/login {email, password, appId:'helm'} -> {accessToken, refreshToken}
//  - helm_api: POST /v1/app/helm/fn/helm_api {action, payload}, Bearer access token; the edge stamps x-nexus-app-key.
//    Responses are the dispatcher envelope {success, message, data}; `data` comes back with keys camelCased
//    (SQL rows arrive snake_case, handler fields camelCase — the Mac app's decoder does the same).
//  - Nexus refresh tokens are SINGLE-USE (family reuse revocation). Every refresh persists the rotated pair before
//    anything else runs, refreshes are single-flight (one in this tab, one across tabs via Web Locks), and a tab
//    whose refresh token was already rotated by another tab adopts the stored pair instead of replaying the old one.

import { APP_ID, AUTH_URL, DEV_APP_KEY, HELM_FN_URL } from './config';

export class ApiError extends Error {
  status: number;
  code?: string;
  body: unknown;
  constructor(status: number, message: string, code?: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

const ACCESS_KEY = 'helm.jwt';
const REFRESH_KEY = 'helm.refresh';

function storage(): Storage | null {
  try { return typeof localStorage === 'undefined' ? null : localStorage; } catch { return null; }
}
export function getToken(): string | null { return storage()?.getItem(ACCESS_KEY) ?? null; }
export function getRefreshToken(): string | null { return storage()?.getItem(REFRESH_KEY) ?? null; }
export function isAuthed(): boolean { return !!getToken(); }

/** Persist the pair atomically. A missing refresh token keeps the old one (never drop the only way back in). */
export function setTokens(access: string, refresh?: string | null): void {
  const s = storage(); if (!s) return;
  s.setItem(ACCESS_KEY, access);
  if (refresh) s.setItem(REFRESH_KEY, refresh);
}
export function clearTokens(): void {
  const s = storage(); if (!s) return;
  s.removeItem(ACCESS_KEY); s.removeItem(REFRESH_KEY);
}

type Listener = () => void;
const authLost = new Set<Listener>();
/** Called when a session can no longer be refreshed (the app shows the sign-in page). */
export function onAuthLost(fn: Listener): () => void { authLost.add(fn); return () => { authLost.delete(fn); }; }
function signalAuthLost(): void { clearTokens(); authLost.forEach((f) => { try { f(); } catch { /* listener errors never block */ } }); }

// ---- key normalisation -------------------------------------------------------------------------------------------

/** snake_case -> camelCase keys, deeply. Values (strings, numbers) are never touched. */
export function camelizeKeys<T = unknown>(v: unknown): T {
  if (Array.isArray(v)) return v.map((x) => camelizeKeys(x)) as unknown as T;
  if (v && typeof v === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
      out[k.replace(/_([a-z0-9])/g, (_m, c: string) => c.toUpperCase())] = camelizeKeys(val);
    }
    return out as T;
  }
  return v as T;
}

async function readJson(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return undefined;
  try { return JSON.parse(text); } catch { throw new ApiError(res.status, `Non-JSON response: ${text.slice(0, 160)}`); }
}

function messageOf(body: unknown, fallback: string): string {
  const b = body as { message?: string | string[]; error?: string } | undefined;
  if (!b) return fallback;
  if (Array.isArray(b.message)) return b.message.join(' ') || fallback;
  return b.message || b.error || fallback;
}
const codeOf = (body: unknown): string | undefined => (body as { code?: string } | undefined)?.code;

// ---- single-flight refresh ---------------------------------------------------------------------------------------

export type RefreshResult = 'ok' | 'refused' | 'unavailable';
let inflight: Promise<RefreshResult> | null = null;

async function withLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = (globalThis as { navigator?: { locks?: { request: <R>(name: string, cb: () => Promise<R>) => Promise<R> } } }).navigator?.locks;
  return locks ? locks.request('helm-refresh', fn) : fn();
}

/**
 * Refresh the session. `usedRefresh` is the refresh token the failed request was made under; when storage now holds a
 * different one, another tab already rotated the pair and we just adopt it.
 */
export function refreshAccess(usedRefresh: string | null): Promise<RefreshResult> {
  if (!inflight) inflight = withLock(() => doRefresh(usedRefresh)).finally(() => { inflight = null; });
  return inflight;
}

async function doRefresh(usedRefresh: string | null): Promise<RefreshResult> {
  const rt = getRefreshToken();
  if (!rt) return 'refused';
  if (usedRefresh && rt !== usedRefresh) return isAuthed() ? 'ok' : 'refused';   // another tab already rotated the pair
  let res: Response;
  try {
    res = await fetch(AUTH_URL('refresh'), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: rt, appId: APP_ID }),
    });
  } catch { return 'unavailable'; }   // offline: keep the pair, try again later
  if (res.status >= 500) return 'unavailable';   // server trouble: never throw the session away for that
  if (!res.ok) {
    // Refused. If another tab rotated meanwhile the stored token has moved on; otherwise the session is over.
    if (getRefreshToken() !== rt) return isAuthed() ? 'ok' : 'refused';
    return 'refused';
  }
  const data = (await readJson(res).catch(() => null)) as { accessToken?: string; refreshToken?: string } | null;
  if (!data?.accessToken) return 'unavailable';
  setTokens(data.accessToken, data.refreshToken);   // persist the rotated pair FIRST
  return 'ok';
}

// ---- helm_api ----------------------------------------------------------------------------------------------------

function headers(): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  const t = getToken();
  if (t) h.Authorization = `Bearer ${t}`;
  if (DEV_APP_KEY) h['x-nexus-app-key'] = DEV_APP_KEY;
  return h;
}

interface Envelope<T> { success?: boolean; message?: string | string[]; data?: T; code?: string }

/** One helm_api call with a single retry across a silent token refresh. */
export async function callFn<T = unknown>(action: string, payload: Record<string, unknown> = {}): Promise<T> {
  const usedRefresh = getRefreshToken();
  const send = (): Promise<Response> => fetch(HELM_FN_URL, {
    method: 'POST', headers: headers(), body: JSON.stringify({ action, payload }),
  });
  let res = await send();
  if (res.status === 401 && usedRefresh) {
    const r = await refreshAccess(usedRefresh);
    if (r === 'ok') res = await send();
    else if (r === 'unavailable') throw new ApiError(503, 'Cannot reach the sign-in service right now — try again in a moment.', 'REFRESH_UNAVAILABLE');
  }
  if (res.status === 401) {
    signalAuthLost();
    throw new ApiError(401, 'Your session has expired. Please sign in again.', 'UNAUTHORIZED');
  }
  const body = (await readJson(res).catch((e) => { if (res.ok) throw e; return undefined; })) as Envelope<T> | undefined;
  if (!res.ok) throw new ApiError(res.status, messageOf(body, `HTTP ${res.status}`), codeOf(body), body);
  if (body && typeof body === 'object' && 'success' in body && body.success === false) {
    throw new ApiError(400, messageOf(body, 'Request failed'), codeOf(body), body);
  }
  const data = body && typeof body === 'object' && 'data' in body ? body.data : body;
  return camelizeKeys<T>(data);
}

// ---- sign-in -----------------------------------------------------------------------------------------------------

export async function loginPassword(email: string, password: string): Promise<void> {
  const res = await fetch(AUTH_URL('login'), {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, appId: APP_ID }),
  });
  const body = (await readJson(res).catch(() => undefined)) as { accessToken?: string; refreshToken?: string; message?: string; code?: string } | undefined;
  if (body?.code === 'PASSWORD_CHANGE_REQUIRED') {
    throw new ApiError(res.status, 'This account must set a new password before its first sign-in. Use the admin console to set one.', body.code);
  }
  if (!res.ok || !body?.accessToken) throw new ApiError(res.status, messageOf(body, 'Sign-in failed'), body?.code);
  setTokens(body.accessToken, body.refreshToken);
}

export function logout(): void { clearTokens(); }

// ---- SSE-free chat: the Assistant is request/response (assistant_send) -------------------------------------------
